import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";

import { isAdmin } from "../../../functions";
import {
  DEFAULT_FILED_CONFIG,
  getFiledConfig,
  saveFiledConfig,
} from "../../../services/backend/FiledConfigService";
import CancelEmailEditor from "./CancelEmailEditor";

import { pqrsdText } from "../../pqrsd/pqrsdLabel";
// The screen's sections. The tab lives in the URL (?tab=) so a link can open
// the email editor directly.
const TABS = [
  { value: "general", label: "General", icon: <TextSnippetIcon fontSize="small" /> },
  { value: "anulacion", label: "Anulación", icon: <BlockIcon fontSize="small" /> },
  { value: "correo", label: "Correo de anulación", icon: <MailOutlineIcon fontSize="small" /> },
];

// Who each switch emails, in the order the function collects addresses.
const AUDIENCE_SWITCHES = [
  {
    key: "pqrsdCitizen",
    label: "Ciudadanos de PQRSD",
    help: "Quien presentó una PQRSD anulada recibe el correo de PQRSD, con el enlace a su seguimiento.",
  },
  { key: "recipients", label: "Destinatarios", help: "Las personas a las que se envió el radicado." },
  { key: "carbonCopy", label: "Copias", help: "Los correos que recibieron copia del radicado." },
  { key: "senders", label: "Remitentes", help: "Quienes firmaron o enviaron el radicado." },
];

const sameConfig = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function FiledConfig() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === searchParams.get("tab")) ? searchParams.get("tab") : "general";

  const [config, setConfig] = useState(null);
  // What is stored, to tell whether there is anything to save or discard.
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // The stored config couldn't be read: the screen shows the defaults, and
  // saving them would silently overwrite what is stored (turning the archiver
  // switch back on, say), so saving waits for a successful read.
  const [loadFailed, setLoadFailed] = useState(false);
  const [snack, setSnack] = useState(false);

  // Role-based, not permission-based: existing admins predate FILED.CONFIG and
  // so don't carry it in their stored permissions snapshot.
  const allowed = isAdmin(user);

  const load = useCallback(async () => {
    setConfig(null);
    const stored = await getFiledConfig();
    setError(!stored);
    setLoadFailed(!stored);
    setConfig(stored || DEFAULT_FILED_CONFIG);
    setSaved(stored || DEFAULT_FILED_CONFIG);
  }, []);

  useEffect(() => {
    if (!allowed) {
      navigate("/home");
      return;
    }
    load();
  }, [allowed, navigate, load]);

  const dirty = !!config && !!saved && !sameConfig(config, saved);

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (evt) => {
      evt.preventDefault();
      evt.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const handleSave = async () => {
    setSaving(true);
    const ok = await saveFiledConfig(config);
    setSaving(false);
    setError(!ok);
    if (ok) {
      setSaved(config);
      setSnack(true);
    }
  };

  const setNotices = (updater) =>
    setConfig((prev) => ({
      ...prev,
      cancelNotifications: typeof updater === "function" ? updater(prev.cancelNotifications) : updater,
    }));

  if (!allowed) return null;

  return (
    <Container maxWidth={tab === "correo" ? "xl" : "md"} sx={{ pt: 4, pb: 12 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Typography variant="h4">Configuración de radicados</Typography>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, next) => setSearchParams({ tab: next }, { replace: true })}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} icon={t.icon} iconPosition="start" sx={{ minHeight: 48 }} />
        ))}
      </Tabs>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={loadFailed ? <Button color="inherit" size="small" onClick={load}>Reintentar</Button> : null}
        >
          {loadFailed
            ? "No se pudo leer la configuración guardada. Se muestran los valores por defecto y no se puede guardar hasta leerla."
            : "No se pudo guardar la configuración. Verifica tus permisos e intenta de nuevo."}
        </Alert>
      )}

      {!config ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === "general" && <OcrSection config={config} setConfig={setConfig} />}
          {tab === "anulacion" && (
            <>
              <CancelPermissionSection config={config} setConfig={setConfig} />
              <CancelNotificationSection
                notices={config.cancelNotifications}
                setNotices={setNotices}
                onEditEmail={() => setSearchParams({ tab: "correo" }, { replace: true })}
              />
            </>
          )}
          {tab === "correo" && (
            <CancelEmailEditor notices={config.cancelNotifications} setNotices={setNotices} />
          )}
        </>
      )}

      {/* One save for every tab, always in reach. */}
      {config && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            px: { xs: 2, sm: 4 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            borderRadius: 0,
          }}
        >
          {dirty && <Chip size="small" color="warning" label="Tienes cambios sin guardar" />}
          <Button onClick={() => setConfig(saved)} disabled={!dirty || saving}>
            Descartar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!dirty || saving || loadFailed}>
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </Paper>
      )}

      <Snackbar
        open={snack}
        autoHideDuration={4000}
        onClose={() => setSnack(false)}
        message="Configuración guardada"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Container>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {icon}
      <Typography variant="h6">{children}</Typography>
    </Stack>
  );
}

function OcrSection({ config, setConfig }) {
  return (
    <Card>
      <CardContent>
        <SectionTitle icon={<TextSnippetIcon color="primary" />}>Extracción de texto (OCR)</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Al radicar un documento, el sistema extrae automáticamente su texto y lo guarda con el
          radicado. Ese texto es lo que permite encontrar un radicado buscando por su contenido, y
          no solo por el número, el asunto o el remitente.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!config.ocrEnabled}
              onChange={(evt) => setConfig((prev) => ({ ...prev, ocrEnabled: evt.target.checked }))}
            />
          }
          label={config.ocrEnabled ? "Activada" : "Desactivada"}
        />
        {!config.ocrEnabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Con la extracción desactivada, los radicados nuevos no se podrán buscar por el texto del
            documento. Los radicados ya procesados conservan su texto y seguirán apareciendo en la
            búsqueda.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function CancelPermissionSection({ config, setConfig }) {
  return (
    <Card>
      <CardContent>
        <SectionTitle icon={<BlockIcon color="primary" />}>Quién puede anular</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Los administradores siempre pueden anular radicados. Aquí decides si también puede hacerlo
          el rol <strong>Archivo</strong>.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!config.archiverCanCancel}
              onChange={(evt) => setConfig((prev) => ({ ...prev, archiverCanCancel: evt.target.checked }))}
            />
          }
          label="El rol Archivo puede anular radicados"
        />
        {!config.archiverCanCancel && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Los usuarios con el rol Archivo verán el botón Anular deshabilitado y el sistema rechazará
            sus anulaciones, aunque tengan el permiso asignado individualmente.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function CancelNotificationSection({ notices, setNotices, onEditEmail }) {
  const setNotice = (key, value) => setNotices((prev) => ({ ...prev, [key]: value }));
  const noAudience = AUDIENCE_SWITCHES.every(({ key }) => !notices[key]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <SectionTitle icon={<SendIcon color="primary" />}>Aviso por correo</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cuando se anula un radicado <strong>finalizado</strong>, se avisa por correo a quienes lo
          recibieron. Un radicado anulado antes de finalizarse nunca se envió a nadie, así que no
          genera aviso. Antes de anular, la persona ve a quién se le enviará.
        </Typography>
        <FormControlLabel
          control={<Switch checked={!!notices.enabled} onChange={(evt) => setNotice("enabled", evt.target.checked)} />}
          label={notices.enabled ? "Aviso activado" : "Aviso desactivado"}
        />

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Enviar a
        </Typography>
        <FormGroup>
          {AUDIENCE_SWITCHES.map(({ key, label, help }) => (
            <FormControlLabel
              key={key}
              disabled={!notices.enabled}
              control={
                <Switch size="small" checked={!!notices[key]} onChange={(evt) => setNotice(key, evt.target.checked)} />
              }
              label={
                <Box>
                  <Typography variant="body2">{pqrsdText(label)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pqrsdText(help)}
                  </Typography>
                </Box>
              }
              sx={{ my: 0.5, alignItems: "flex-start", "& .MuiSwitch-root": { mt: -0.25 } }}
            />
          ))}
        </FormGroup>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Contenido
        </Typography>
        <FormControlLabel
          disabled={!notices.enabled}
          control={
            <Switch
              size="small"
              checked={!!notices.includeReason}
              onChange={(evt) => setNotice("includeReason", evt.target.checked)}
            />
          }
          label="Incluir el motivo de la anulación"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          El correo nunca indica quién anuló el radicado.
        </Typography>

        {notices.enabled && noAudience && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            El aviso está activado pero no hay ningún grupo seleccionado: no se enviará ningún correo.
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" startIcon={<MailOutlineIcon />} onClick={onEditEmail}>
            Personalizar el correo
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
